import { AccountSettings } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import Copyright from "03AdminPages/99Parts/Copyright";
import { useSignal } from "99Tools/useModel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { validate } from "email-validator";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

const MyPaper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const MyAvatar = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const MyForm = styled("form")(({ theme }) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: theme.spacing(1),
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const MessageText = styled("div")(({ theme }) => ({
  color: "#00f",
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const ResendButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 0),
}));

const DescriptionText = styled("div")(({ theme }) => ({
  fontSize: "0.95rem",
  color: theme.palette.grey[800],
  margin: theme.spacing(2, 0, 2),
}));

const ConfirmPage = () => {
  const a = useContext(AppContext).account;
  const navigate = useNavigate();
  const [account] = useState<AccountSettings>(new AccountSettings());
  const [message, setMessage] = useState("");
  const [info, setInfo] = useState("");
  const redraw = useSignal();

  const handleConfirm = () => {
    setInfo("");
    if (account.user === "") {
      setMessage("メールアドレスを入力してください。");
      return;
    }
    if (!validate(account.user)) {
      setMessage("有効なメールアドレスを入力してください。");
      return;
    }
    if (account.code === "") {
      setMessage("コードを入力してください。");
      return;
    }
    a.ConfirmSignUp(account)
      .then(() => {
        navigate("/login/");
      })
      .catch((err: any) => {
        setMessage(
          "コードが誤っているか有効期限切れです。訂正して再送するか新しいコードを発行してください。もしくは、パスワードを忘れた場合は、トップ画面から「パスワードを忘れた場合」をクリックしてください。"
        );
        console.log("コード確認エラー");
        console.error(err);
      });
  };

  const handleResendCode = () => {
    if (account.user === "") {
      setMessage("メールアドレスを入力してください。");
      return;
    }
    if (!validate(account.user)) {
      setMessage("有効なメールアドレスを入力してください。");
      return;
    }
    setMessage("");
    a.ResendConfirmCode(account)
      .then(() => {
        setInfo("コードを再送信しました。");
      })
      .catch((err: any) => {
        setMessage("コードの再送信に失敗しました。管理者にご連絡ください。");
        console.log("コード再送信エラー");
        console.error(err);
      });
  };

  const handleChangeMail = (e: any) => {
    account.user = e.target.value;
    setMessage("");
    redraw();
  };

  const handleChangeCode = (e: any) => {
    account.code = e.target.value;
    setMessage("");
    redraw();
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <MyPaper>
        <Typography component="h1" variant="h5">
          漏水みはり番監視システム
        </Typography>
        <MyAvatar>
          <LockOutlinedIcon />
        </MyAvatar>
        <Typography component="h1" variant="h5">
          認証コードの入力
        </Typography>
        <MyForm noValidate>
          <DescriptionText>
            登録したメールアドレス宛にお送りした認証コードを入力ください。見つからない場合は、迷惑メールフォルダをご確認ください
          </DescriptionText>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            autoFocus
            id="email"
            label="メールアドレス"
            name="email"
            autoComplete="email"
            inputProps={{ maxLength: 128 }}
            value={account.user}
            onChange={handleChangeMail}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirm();
              }
            }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="code"
            label="コードを入力"
            name="code"
            autoComplete="off"
            inputProps={{ inputMode: "numeric", maxLength: 128 }}
            value={account.code}
            onChange={handleChangeCode}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirm();
              }
            }}
          />
          {0 < message.length ? <ErrorText>{message}</ErrorText> : null}
          <ResendButton variant="text" type="button" onClick={handleResendCode}>
            メールアドレスを入力してコードを再送信する
          </ResendButton>
          {0 < info.length ? <MessageText>{info}</MessageText> : null}
          <SubmitButton
            fullWidth
            type="button"
            variant="contained"
            color="primary"
            onClick={() => handleConfirm()}
          >
            登録
          </SubmitButton>
        </MyForm>
      </MyPaper>
      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
};

export default ConfirmPage;
